import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import { AppBar, Toolbar, Typography, Box, Button, IconButton, Drawer, List, ListItem, ListItemButton, useMediaQuery, Chip } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import logo from "./components/logo.svg";
import Home from "./pages/Home";
import About from "./pages/About";
import Oferta from "./pages/Offer";
import Kontakt from "./pages/Contact";
import NotFound from "./pages/NotFound";
import PolitykaPrywatnosci from "./pages/PrivacyPolicy";
import Login from "./pages/admin/Login";
import Dashboard from "./pages/admin/Dashboard";
import ProtectedRoute from "./components/ProtectedRoute";
import { AuthProvider } from "./context/AuthContext";
import { LanguageProvider, useLang } from "./context/LanguageContext";

function ScrollToTopAndTitle() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  useEffect(() => {
    const titles: Record<string, string> = {
      "/": "Mazur Masz",
      "/oferta": "Oferta – Mazur Masz",
      "/o-nas": "O nas – Mazur Masz",
      "/kontakt": "Kontakt – Mazur Masz",
      "/polityka-prywatnosci": "Polityka prywatności – Mazur Masz",
      "/admin/login": "Logowanie – Mazur Masz",
      "/admin": "Panel administracyjny – Mazur Masz",
    };
    document.title = titles[pathname] || "Mazur Masz";
  }, [pathname]);

  return null;
}

function LangSwitch() {
  const { lang, setLang } = useLang();
  return (
    <Chip
      label={lang === "pl" ? "EN" : "PL"}
      onClick={() => setLang(lang === "pl" ? "en" : "pl")}
      size="small"
      sx={{
        color: "#f0f0f0",
        border: "1px solid rgba(255,255,255,0.3)",
        cursor: "pointer",
        fontWeight: 700,
        fontSize: "0.75rem",
        "&:hover": { borderColor: "#fff", backgroundColor: "rgba(255,255,255,0.1)" },
      }}
    />
  );
}

function AppInner() {
  const [scrolled, setScrolled] = useState<boolean>(false);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const isMobile = useMediaQuery("(max-width: 900px)");
  const { pathname } = useLocation();
  const { t } = useLang();

  const pages: Array<{ label: string; path: string }> = [
    { label: t("nav.home"), path: "/" },
    { label: t("nav.offer"), path: "/oferta" },
    { label: t("nav.about"), path: "/o-nas" },
    { label: t("nav.contact"), path: "/kontakt" },
  ];

  useEffect(() => {
    const handleScroll = (): void => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const isAdmin = pathname.startsWith("/admin");

  return (
    <AuthProvider>
      <Box sx={{ backgroundColor: "#1a1a1a", minHeight: "100vh" }}>
        <ScrollToTopAndTitle />
        {!isAdmin && (
          <>
            <AppBar
              position="fixed"
              elevation={scrolled ? 4 : 0}
              sx={{
                transition: "0.3s",
                backgroundColor: scrolled
                  ? "rgba(30,30,30,0.9)"
                  : "rgba(0,0,0,0)",
                backdropFilter: scrolled ? "blur(10px)" : "none",
              }}
            >
              <Toolbar>
                <Box sx={{ flexGrow: 1, display: "flex", alignItems: "center", gap: 1 }}>
                  <Link to="/" style={{ display: "flex", alignItems: "center", gap: 8, textDecoration: "none" }}>
                    <img src={logo} alt="Mazur Masz" height={48} />
                    <Typography variant="h6" sx={{ color: "#f0f0f0" }}>
                      Mazur Masz
                    </Typography>
                  </Link>
                </Box>
                <LangSwitch />
                {isMobile ? (
                  <>
                    <IconButton onClick={() => setDrawerOpen(true)} sx={{ color: "#f0f0f0" }}>
                      <MenuIcon />
                    </IconButton>
                    <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
                      <Box sx={{ width: 250, backgroundColor: "#1a1a1a", height: "100%", pt: 2 }}>
                        <List>
                          {pages.map((p) => (
                            <ListItem key={p.path} disablePadding>
                              <ListItemButton
                                component={Link}
                                to={p.path}
                                onClick={() => setDrawerOpen(false)}
                                sx={{ color: "#f0f0f0" }}
                              >
                                {p.label}
                              </ListItemButton>
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    </Drawer>
                  </>
                ) : (
                  pages.map((p) => (
                    <Button key={p.path} component={Link} to={p.path} sx={{ color: "#f0f0f0" }}>
                      {p.label}
                    </Button>
                  ))
                )}
              </Toolbar>
            </AppBar>
            <Toolbar />
          </>
        )}

        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/oferta" element={<Oferta />} />
          <Route path="/o-nas" element={<About />} />
          <Route path="/kontakt" element={<Kontakt />} />
          <Route path="/polityka-prywatnosci" element={<PolitykaPrywatnosci />} />
          <Route path="/admin/login" element={<Login />} />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<NotFound />} />
        </Routes>

        {!isAdmin && (
          <Box
            sx={{
              py: 4,
              textAlign: "center",
              borderTop: "1px solid #444",
              mt: 4,
              color: "#bbb",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, mb: 1 }}>
              <img src={logo} alt="Mazur Masz" height={24} />
              <Typography variant="body1" sx={{ color: "#f0f0f0", fontWeight: 600 }}>
                Mazur Masz
              </Typography>
            </Box>
            <Typography variant="body2">
              &copy; {new Date().getFullYear()} Mazur Masz &mdash; {t("footer.copyright")}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              <Link to="/polityka-prywatnosci" style={{ color: "#bbb", textDecoration: "underline" }}>
                {t("footer.privacyPolicy")}
              </Link>
            </Typography>
          </Box>
        )}
      </Box>
    </AuthProvider>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <AppInner />
      </LanguageProvider>
    </BrowserRouter>
  );
}
